import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { isWebHidSupported, WacomStuDriver } from "../utils/wacomStu";

const PRESSURE_THRESHOLD = 35;

function getCanvasLogicalSize(canvas) {
  const ratio = Math.max(window.devicePixelRatio ?? 1, 1);
  return {
    width: canvas.width / ratio,
    height: canvas.height / ratio,
  };
}

/**
 * Gestisce la sessione di firma con tavoletta Wacom STU via WebHID.
 * Disegna direttamente sul canvas di react-signature-canvas senza usare
 * fromDataURL (che su schermi HiDPI comprime l'immagine per il devicePixelRatio).
 */
export function useWacomStuTablet(sigPadRef) {
  const driverRef = useRef(null);
  const isDrawingRef = useRef(false);
  const lastPointRef = useRef({ x: 0, y: 0 });
  const hasTabletInkRef = useRef(false);
  const [tabletActive, setTabletActive] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [deviceLabel, setDeviceLabel] = useState(null);

  /** Indica se la tavoletta ha prodotto inchiostro (per isEmpty override nel componente). */
  const getHasTabletInk = useCallback(() => hasTabletInkRef.current, []);

  const drawOnCanvas = useCallback(
    (pen, driver) => {
      const sigPad = sigPadRef?.current;
      if (!sigPad) return;
      const canvas = sigPad.getCanvas();
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      const { tabletMaxX, tabletMaxY, rawMaxX, rawMaxY } = driver.config;
      const maxX = tabletMaxX || rawMaxX;
      const maxY = tabletMaxY || rawMaxY;
      const { width: logicalW, height: logicalH } = getCanvasLogicalSize(canvas);

      // react-signature-canvas applica ctx.scale(devicePixelRatio): disegnare in pixel
      // canvas.width produce coordinate errate e tratti spuri su schermi HiDPI.
      const scaleX = logicalW / maxX;
      const scaleY = logicalH / maxY;
      const drawX = pen.x * scaleX;
      const drawY = pen.y * scaleY;

      if (pen.sw) {
        hasTabletInkRef.current = true;
        const pressRaw = pen.pressRaw ?? pen.press * 1023;
        if (!isDrawingRef.current) {
          isDrawingRef.current = true;
          lastPointRef.current = { x: drawX, y: drawY };
        } else {
          ctx.beginPath();
          ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
          ctx.lineTo(drawX, drawY);
          ctx.strokeStyle = "#000000";
          ctx.lineWidth = Math.min(4, 1.5 + (pressRaw - PRESSURE_THRESHOLD) / 200);
          ctx.lineCap = "round";
          ctx.lineJoin = "round";
          ctx.stroke();
          lastPointRef.current = { x: drawX, y: drawY };
        }
      } else if (isDrawingRef.current) {
        isDrawingRef.current = false;
        // Non chiamare fromDataURL qui: su schermi HiDPI (devicePixelRatio > 1)
        // comprimerebbe il disegno nell'angolo superiore sinistro ad ogni pen-up.
      }
    },
    [sigPadRef]
  );

  const startTablet = useCallback(async () => {
    if (!isWebHidSupported()) {
      toast.error("Browser non compatibile. Usa Chrome o Edge per la tavoletta firma.");
      return false;
    }

    setConnecting(true);
    try {
      if (!driverRef.current) {
        driverRef.current = new WacomStuDriver();
      }
      const driver = driverRef.current;

      const connected = await driver.connect();
      if (!connected) {
        toast.info("Nessuna tavoletta selezionata.");
        return false;
      }

      driver.onPenData((pen) => drawOnCanvas(pen, driver));

      sigPadRef?.current?.clear();
      hasTabletInkRef.current = false;
      isDrawingRef.current = false;
      await driver.clearScreen();

      setDeviceLabel(driver.deviceName);
      setTabletActive(true);
      toast.success(`Tavoletta ${driver.deviceName} pronta — firma con il pennino.`);
      return true;
    } catch (err) {
      console.error("Errore connessione tavoletta:", err);
      toast.error("Impossibile connettersi alla tavoletta. Verifica cavo USB e permessi.");
      return false;
    } finally {
      setConnecting(false);
    }
  }, [drawOnCanvas, sigPadRef]);

  const stopTablet = useCallback(async () => {
    isDrawingRef.current = false;
    if (driverRef.current) {
      try {
        await driverRef.current.setInking(false);
      } catch {
        /* ignore */
      }
    }
    setTabletActive(false);
    setDeviceLabel(null);
  }, []);

  const clearSignature = useCallback(async () => {
    sigPadRef?.current?.clear();
    hasTabletInkRef.current = false;
    isDrawingRef.current = false;
    if (driverRef.current?.isConnected) {
      try {
        await driverRef.current.clearScreen();
      } catch {
        /* ignore */
      }
    }
  }, [sigPadRef]);

  useEffect(() => {
    return () => {
      driverRef.current?.disconnect();
      driverRef.current = null;
    };
  }, []);

  return {
    webHidSupported: isWebHidSupported(),
    tabletActive,
    connecting,
    deviceLabel,
    startTablet,
    stopTablet,
    clearSignature,
    getHasTabletInk,
  };
}
